import type { BoxProps } from '@material-ui/core/Box';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import type { FunctionComponent } from 'react';
import { isValidElement, useMemo } from 'react';
import styled from 'styled-components';
import map from 'lodash/map';
import filter from 'lodash/filter';

import { AVERAGE_MONTHLY_SAVINGS, AVERAGE_RATE_DEDUCTION } from '@jerry-serverless/refi-common/const/savings';

import { DeviceTypeType, type AppData, type SelectedServiceType } from '../../types/app_data';
import { SingleStarIcon } from '../icon/single_star_icon/SingleStarIcon';
import { isGarageFlow } from '../../utils/garage';

import { PolicySoldLabel, PolicySoldNumber } from './components/PolicySold';
import { PolicySavingLabel, PolicySavingsNumber } from './components/PolicySavings';

interface TrustTextProps extends BoxProps, Pick<AppData, 'mobileOS'> {
  className?: string;
  selectedService?: SelectedServiceType;
  trustCopyList?: TrustCopy[];
  lightVersion?: boolean;
  alignCenter?: boolean;
  appData?: AppData;
}

export interface TrustCopy {
  value: React.ReactNode;
  label: React.ReactNode;
  // used in the light version
  shortLabel?: string;
  /** Whether we should show a superscript 1. */
  sup?: boolean;
  prefix?: React.ReactNode;
}

export const TrustBuildingInfo = {
  rating: {
    label: '22k Reviews',
    value: '4.7',
    prefix: <SingleStarIcon />,
  },
  garageRating: {
    label: '24k Ratings',
    value: '4.7',
    prefix: <SingleStarIcon />,
  },
  review: { label: 'Reviews', value: '24k' },
  appStoreRating: {
    label: 'App Store Rating',
    value: '4.7',
    shortLabel: 'Rating',
    prefix: <SingleStarIcon />,
  },
  googlePlayReview: { label: 'Reviews', value: '11.7k' },
  appDownloads: { label: 'App downloads', value: '4M+', shortLabel: 'Downloads' },
  activeCustomers: { label: 'Active customers', value: '5M+' },
  customers: { label: 'Customers', value: '4M' },
  tripsLastMonth: { label: 'Trips last month', value: '1,249,859' },
  carsGaraged: { label: 'Cars garaged', value: '6M+' },
  bbbRating: { label: 'BBB Rating', value: 'A+' },
  avgInterestRateReduction: { label: 'Avg. interest rate improvement', value: AVERAGE_RATE_DEDUCTION, sup: true },
  avgMonthlyPaymentReduction: {
    label: 'Avg. monthly payment reduction',
    value: AVERAGE_MONTHLY_SAVINGS,
    sup: true,
  },
  boughtPolicies: { label: <PolicySoldLabel />, value: <PolicySoldNumber /> },
  boughtPoliciesRecently: { label: 'Bought policies recently', value: '40k+', shortLabel: 'Policies Sold' },
  saved: { label: <PolicySavingLabel />, value: <PolicySavingsNumber /> },
} as const;

/**
 * Component to display some trust text in phone page
 */
const UnstyledTrustText: FunctionComponent<TrustTextProps> = ({
  className,
  selectedService,
  trustCopyList: trustCopyListProp,
  lightVersion = false,
  alignCenter = false,
  mobileOS,
  appData,
  ...boxProps
}): JSX.Element => {
  const trustCopyList: TrustCopy[] = useMemo((): TrustCopy[] => {
    if (trustCopyListProp) {
      return trustCopyListProp;
    }

    // On android we show google play reviews count. On other platforms, we show app store rating.
    const ratingOrReviews =
      mobileOS === DeviceTypeType.Android ? TrustBuildingInfo.googlePlayReview : TrustBuildingInfo.rating;

    if (filter(selectedService, (selected) => !!selected).length > 1) {
      return [ratingOrReviews, TrustBuildingInfo.activeCustomers, TrustBuildingInfo.saved];
    } else if (selectedService?.garage) {
      if (appData && isGarageFlow(appData)) {
        return [TrustBuildingInfo.garageRating, TrustBuildingInfo.activeCustomers, TrustBuildingInfo.bbbRating];
      }
      return [TrustBuildingInfo.garageRating, TrustBuildingInfo.activeCustomers, TrustBuildingInfo.carsGaraged];
    } else if (selectedService?.dwep) {
      return [ratingOrReviews, TrustBuildingInfo.activeCustomers, TrustBuildingInfo.tripsLastMonth];
    } else if (selectedService?.refi) {
      return [
        ratingOrReviews,
        TrustBuildingInfo.avgInterestRateReduction,
        TrustBuildingInfo.avgMonthlyPaymentReduction,
      ];
    }
    return [ratingOrReviews, TrustBuildingInfo.boughtPolicies, TrustBuildingInfo.saved];
  }, [appData, mobileOS, selectedService, trustCopyListProp]);

  return (
    <Box className={className} {...boxProps}>
      <Box className={lightVersion ? 'trustContainer-light' : 'trustContainer'}>
        {map(trustCopyList, ({ value, label, shortLabel, sup, prefix }, index) => (
          <Box
            key={(isValidElement(label) ? label?.props?.key : label) + index}
            display="flex"
            flexDirection="column"
            flexBasis="calc(100% / 3)"
            alignItems={lightVersion || alignCenter ? 'center' : 'start'}
          >
            <Box display="flex" mb={0.25}>
              {prefix && (
                <Box display="flex" mr={0.5}>
                  {prefix}
                </Box>
              )}
              <Typography
                className="trustValue"
                style={lightVersion ? { fontWeight: 500, lineHeight: '18px', fontSize: '14px' } : {}}
              >
                {value}
              </Typography>
            </Box>
            <Typography className="trustLabel" style={lightVersion ? { fontSize: 10, color: '#6E7E91' } : {}}>
              {lightVersion && shortLabel ? shortLabel : label}
              {sup && (
                <sup
                  style={{
                    // <sup> extends the line height, making the label not vertically aligend with others.
                    // Setting its line height to 0 to fix this.
                    lineHeight: 0,
                  }}
                >
                  1
                </sup>
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const TrustText = styled(UnstyledTrustText)`
  .trustContainer {
    background-color: #f8f9fa;
    position: absolute;
    width: 100%;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: space-between;
    padding: 1rem;
    gap: 0.5rem;

    &:not(.trustContainer-light) {
      background-color: transparent;
      position: static;
      padding: 0;
    }
  }
  .trustContainer-light {
    width: 100%;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: space-between;
    padding: 1rem;
    gap: 0.5rem;
    align-items: top;

    .trustLabel {
      text-align: center;
    }
  }

  .trustValue {
    font-size: 1rem;
    line-height: 0.75rem;
    color: #141d2b;
    font-weight: 700;
  }

  .trustLabel {
    font-size: 0.75rem;
    color: #6e7e91;
    line-height: 1rem;
  }
`;
